import React, { FC, useMemo } from 'react';

import { Empty, Table, Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table';

import {
  TCrmCohortRow,
  TCrmCohorts,
  TCrmSchoolStatus,
} from '../../../../shared/common/api/endpoints/crmAnalyticsApi';
import EChart from '../../../../shared/common/components/reports/EChart';
import { cohortChartOption } from '../chartOptions';
import { STATUS_META } from '../constants';
import styles from '../CrmAnalytics.module.scss';
import { bucketLabel, formatDays, formatNumber, formatPercent } from '../utils';
import { HintTitle } from './common';

interface Props {
  cohorts: TCrmCohorts | null;
}

// Статусы когорты сейчас — в порядке «лучше → хуже»
const NOW_STATUSES: TCrmSchoolStatus[] = [ 'paying', 'debt', 'test', 'grace_alive', 'suspended', 'grace_dead', 'disconnected' ];

const CohortsTab: FC<Props> = ({ cohorts }) => {
  const rows = cohorts?.rows ?? [];
  const granularity = cohorts?.granularity ?? 'month';
  const option = useMemo(() => cohortChartOption(rows, granularity), [ rows, granularity ]);

  if (!cohorts) return <Empty description="Нет данных" />;

  const windowDays = rows[0]?.windows.map((window) => window.days) ?? [];

  const columns: ColumnsType<TCrmCohortRow> = [
    {
      title: 'Подключились',
      dataIndex: 'bucket',
      fixed: 'left',
      width: 120,
      render: (bucket: string) => bucketLabel(bucket, granularity),
    },
    {
      title: <HintTitle title="Школ" hint="Подключено школ в отрезке" />,
      dataIndex: 'connected',
      align: 'right',
      render: formatNumber,
    },
    {
      title: <HintTitle title="Стали покупателями" hint="Был первый реальный платёж — когда угодно после подключения" />,
      key: 'paid_ever',
      align: 'right',
      render: (_, row) => (row.connected > 0 ? `${formatNumber(row.paid_ever)} · ${formatPercent(row.conversion)}` : '—'),
    },
    ...windowDays.map((days, i) => ({
      title: (
        <HintTitle
          title={`За ${days} дн.`}
          hint={days === 14
            ? 'Оплатили за время теста — 14 дней с подключения'
            : `Оплатили в первые ${days} дней с подключения`}
        />
      ),
      key: `window_${days}`,
      align: 'right' as const,
      render: (_: unknown, row: TCrmCohortRow) => {
        const window = row.windows[i];
        if (!window || row.connected === 0) return '—';
        if (!window.mature) {
          return (
            <Tooltip title="Окно ещё не прошло у всей когорты — значение вырастет">
              <span className={styles.muted}>{formatNumber(window.paid)}*</span>
            </Tooltip>
          );
        }
        return `${formatNumber(window.paid)} · ${formatPercent(window.conversion)}`;
      },
    })),
    {
      title: <HintTitle title="Дней до оплаты" hint="Медиана дней от подключения до первого реального платежа" />,
      dataIndex: 'median_days_to_pay',
      align: 'right',
      render: formatDays,
    },
    ...NOW_STATUSES.map((status) => ({
      title: <HintTitle title={`Сейчас: ${STATUS_META[status].label.toLowerCase()}`} hint={STATUS_META[status].hint} />,
      key: `now_${status}`,
      align: 'right' as const,
      render: (_: unknown, row: TCrmCohortRow) => formatNumber(row.now[status]),
    })),
  ];

  const sumNow = (status: TCrmSchoolStatus) => rows.reduce((sum, row) => sum + row.now[status], 0);

  return (
    <div>
      <div className={styles.chartCard} style={{ marginBottom: 16 }}>
        <div className={styles.chartHeader}>
          <div>
            <div className={styles.chartTitle}>Доля ставших покупателями</div>
            <div className={styles.chartHint}>
              От подключившихся в отрезке: оплатили за 14 дней (тест), 30 и 90 дней. Окна, которые ещё не прошли, не показаны
            </div>
          </div>
        </div>
        <EChart option={option} style={{ height: 320 }} maxDataPoints={0} lazyLoad={false} />
      </div>

      <Table<TCrmCohortRow>
        className={styles.table}
        rowKey="bucket"
        size="small"
        columns={columns}
        dataSource={rows}
        pagination={false}
        scroll={{ x: 'max-content' }}
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0}><b>Итого</b></Table.Summary.Cell>
              <Table.Summary.Cell index={1} align="right">{formatNumber(cohorts.totals.connected)}</Table.Summary.Cell>
              <Table.Summary.Cell index={2} align="right">
                {`${formatNumber(cohorts.totals.paid_ever)} · ${formatPercent(cohorts.totals.conversion)}`}
              </Table.Summary.Cell>
              {windowDays.map((days, i) => (
                <Table.Summary.Cell key={days} index={3 + i} align="right">
                  {formatNumber(rows.reduce((sum, row) => sum + (row.windows[i]?.paid ?? 0), 0))}
                </Table.Summary.Cell>
              ))}
              <Table.Summary.Cell index={3 + windowDays.length} align="right">—</Table.Summary.Cell>
              {NOW_STATUSES.map((status, i) => (
                <Table.Summary.Cell key={status} index={4 + windowDays.length + i} align="right">
                  {formatNumber(sumNow(status))}
                </Table.Summary.Cell>
              ))}
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
    </div>
  );
};

export default CohortsTab;
